/**
 * Eligibility Verification Panel
 * Member search + one-click verify + result display.
 * Can be used standalone (with built-in member search) or embedded
 * in a member profile (with memberId prop).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { eligibilityApi, EligibilityResult } from '@/services/api/eligibility';
import { apiClient } from '@/services/apiClient';
import EligibilityResultCard from '@/components/eligibility/EligibilityResultCard';
import { useTabbedChrome } from '@/components/weavein';
import { useTranslation } from 'react-i18next';
import { useCapability } from '@/hooks/useCapabilities';

interface Props {
  memberId?: number;
  showHistory?: boolean;
}

interface MemberSearchResult {
  id: number;
  first_name: string;
  last_name: string;
  medicaid_id?: string;
  date_of_birth?: string;
  county?: string;
}

const EligibilityVerificationPanel: React.FC<Props> = ({ memberId: propMemberId, showHistory = true }) => {
  // TRUS-739: the FFS transition cohort link shows only in orgs with ffs_transition.
  const activeOrgId = Number(localStorage.getItem('active_cbo_id')) || null;
  const { enabled: ffsTransitionEnabled } = useCapability(activeOrgId, 'ffs_transition');
  const tabbed = useTabbedChrome();
  const { t } = useTranslation();
  const [selectedMember, setSelectedMember] = useState<MemberSearchResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MemberSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const [currentResult, setCurrentResult] = useState<EligibilityResult | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  const activeMemberId = propMemberId || selectedMember?.id;

  // Load current eligibility when member is selected
  useEffect(() => {
    if (activeMemberId && activeMemberId > 0) {
      loadCurrentEligibility();
      if (showHistory) loadHistory();
    } else {
      setCurrentResult(null);
      setHistory([]);
    }
  }, [activeMemberId]);

  const searchMembers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setSearching(true);
    try {
      const response = await apiClient.get('/api/members', {
        params: { search: query, limit: 10 }
      });
      const members = response.data?.members || response.data || [];
      setSearchResults(Array.isArray(members) ? members : []);
      setShowDropdown(true);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) searchMembers(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchMembers]);

  const handleSelectMember = (member: MemberSearchResult) => {
    setSelectedMember(member);
    setSearchQuery(`${member.first_name} ${member.last_name}`);
    setShowDropdown(false);
    setError('');
  };

  const loadCurrentEligibility = async () => {
    if (!activeMemberId || activeMemberId <= 0) return;
    setLoading(true);
    try {
      const response = await eligibilityApi.getMemberCurrent(activeMemberId);
      if (response.data?.verification_id) {
        setCurrentResult(response.data);
      }
    } catch {
      // No cached result
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (!activeMemberId || activeMemberId <= 0) return;
    try {
      const response = await eligibilityApi.getMemberHistory(activeMemberId);
      setHistory(response.data?.verifications || []);
    } catch {
      // History load failed
    }
  };

  const handleVerify = async (reason: 'MANUAL' | 'ECM_REFERRAL' | 'ENROLLMENT_CHECK' | 'RENEWAL' = 'MANUAL') => {
    if (!activeMemberId || activeMemberId <= 0) {
      setError('Please select a member first');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const response = await eligibilityApi.verify({ member_id: activeMemberId, reason });
      setCurrentResult(response.data);
      if (showHistory) loadHistory();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      {!tabbed && (
      <h2 className="text-lg font-semibold">Member Eligibility Verification</h2>
      )}

      {/* TRUS-724: org-wide view of who moves to Medi-Cal FFS on 1 Jan 2027.
          TRUS-739: only for orgs with the ffs_transition capability. */}
      {!propMemberId && ffsTransitionEnabled && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'ffs-transition-cohort' }))}
            className="text-sm font-medium text-v2accent-ink hover:underline"
          >
            {t('ffsTransition.entryLink')}
          </button>
        </div>
      )}

      {/* Member Search (only shown when no memberId prop) */}
      {!propMemberId && (
        <div className="bg-white rounded-lg shadow p-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search Member</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name, Medicaid ID, or phone..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!e.target.value) {
                  setSelectedMember(null);
                  setCurrentResult(null);
                  setHistory([]);
                }
              }}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
            {searching && (
              <span className="absolute right-3 top-2.5 text-xs text-gray-400">Searching...</span>
            )}

            {/* Search Results Dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => handleSelectMember(member)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-sm"
                  >
                    <div className="font-medium">{member.first_name} {member.last_name}</div>
                    <div className="text-xs text-gray-500">
                      {member.medicaid_id && <span>CIN: {member.medicaid_id} | </span>}
                      {member.date_of_birth && <span>DOB: {member.date_of_birth} | </span>}
                      {member.county && <span>County: {member.county}</span>}
                      {!member.medicaid_id && !member.date_of_birth && !member.county && <span>ID: {member.id}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Member Info */}
          {selectedMember && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
              <div>
                <span className="font-medium text-sm">{selectedMember.first_name} {selectedMember.last_name}</span>
                {selectedMember.medicaid_id && (
                  <span className="ml-2 text-xs text-gray-600">CIN: {selectedMember.medicaid_id}</span>
                )}
                {selectedMember.county && (
                  <span className="ml-2 text-xs text-gray-600">County: {selectedMember.county}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedMember(null);
                  setSearchQuery('');
                  setCurrentResult(null);
                  setHistory([]);
                }}
                className="text-xs text-gray-500 hover:text-red-600"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {/* Verify Actions */}
      {activeMemberId && activeMemberId > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Medi-Cal Eligibility Check</h3>
            <div className="flex gap-2">
              <button
                onClick={() => handleVerify('MANUAL')}
                disabled={verifying}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {verifying ? 'Verifying...' : 'Verify Now'}
              </button>
              <select
                onChange={(e) => {
                  if (e.target.value) handleVerify(e.target.value as any);
                  e.target.value = '';
                }}
                disabled={verifying}
                className="border rounded-md px-2 py-2 text-sm"
                defaultValue=""
              >
                <option value="" disabled>More options...</option>
                <option value="ECM_REFERRAL">ECM Referral Check</option>
                <option value="ENROLLMENT_CHECK">Enrollment Check</option>
                <option value="RENEWAL">Renewal Check</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm mb-3">
              {error}
            </div>
          )}

          {/* Current Result */}
          {loading ? (
            <div className="text-center py-8 text-gray-400 text-sm">Loading eligibility data...</div>
          ) : currentResult ? (
            <EligibilityResultCard result={currentResult} />
          ) : (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-sm">No eligibility verification on file.</p>
              <p className="text-xs text-gray-400 mt-1">Click "Verify Now" to check Medi-Cal eligibility and MCP assignment.</p>
            </div>
          )}
        </div>
      )}

      {/* No member selected prompt */}
      {(!activeMemberId || activeMemberId <= 0) && !propMemberId && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">Search and select a member above to verify their Medi-Cal eligibility.</p>
          <p className="text-sm text-gray-400 mt-1">Verification checks enrollment status, managed care plan, aid code, and benefits scope.</p>
        </div>
      )}

      {/* History */}
      {showHistory && history.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Verification History</h4>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">MCP</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Channel</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h: any) => (
                  <tr key={h.verification_id} className="border-t">
                    <td className="px-3 py-2 text-xs">{h.verified_at?.split('T')[0]}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        h.eligibility_status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                        h.eligibility_status === 'ESTIMATED' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {h.eligibility_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 truncate max-w-[150px] text-xs">{h.mcp_plan_name || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{h.query_channel?.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{h.request_reason?.replace(/_/g, ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EligibilityVerificationPanel;
